import { API_CREDENTIAL_STATUS, CompaniesHierarchyDataDto } from "@/shared";

import { flattenCompanyChildren } from "./Companies/functions";
import {
  AnalysisProvidersRow,
  MappedAnalysisProvidersRow
} from "./Integrations/AnalysisProviders/types";
import {
  ApiCredentialRow,
  MappedApiCredentialRow
} from "./Integrations/IntegrationsApi/types";
import { IntegrationsRow } from "./Integrations/types";
import {
  MappedUsersInvitationsDataDto,
  UsersInvitationsDataDto
} from "./Users/UsersInvitations/types";

export const mapDriversTableRowData = (data: any) =>
  data.map((dataItem: any) => {
    const schedules = dataItem.scheduleSummaries?.map(
      (schedule: { name: string; scope: number }) => schedule.name
    );

    return {
      ...dataItem,
      schedulesList: schedules,
      lastVehicleNumber: dataItem.lastVehicleNumber
    };
  });

export const mapVehiclesTableRowData = (data: any) =>
  data.map((dataItem: any) => {
    const schedules = dataItem.scheduleSummaries?.map(
      (schedule: { name: string; scope: number }) => schedule.name
    );

    return {
      ...dataItem,
      schedulesList: schedules
    };
  });

export const mapVehicleFilesTableRowData = (data: any) =>
  data.map((dataItem: any) => {
    return {
      id: dataItem.id,
      fileName: dataItem.fileName,
      sourceName: dataItem.sourceName,
      fileTypes: dataItem.schedule.fileTypes,
      number: dataItem.vehicle.number,
      activitiesFrom: dataItem.schedule.activitiesFrom,
      activitiesTo: dataItem.schedule.activitiesTo,
      downloadTime: dataItem.downloadTime,
      imei: dataItem.vehicle.imei
    };
  });

export const mapDriversFilesTableRowData = (data: any) =>
  data.map((dataItem: any) => {
    return {
      id: dataItem.id,
      driverName: dataItem.driver.driverName,
      fileName: dataItem.fileName,
      sourceName: dataItem.sourceName,
      vehicleImei: dataItem.vehicleImei,
      cardNumber: dataItem.driver.cardNumber,
      downloadTime: dataItem.downloadTime
    };
  });

export const mapUserTableRowData = (data: any) =>
  data.map((dataItem: any) => {
    return {
      id: dataItem.id,
      email: dataItem.email,
      // name the property with the original name from response at the mentioned level
      // in this case - "name"
      name: dataItem.company.name,
      role: dataItem.role,
      invitedBy: dataItem.invitedBy,
      invitedAt: dataItem.invitedAt,
      updatedAt: dataItem.updatedAt,
      company: dataItem.company
    };
  });

export const mapIntegrationsTableRowData = (data: IntegrationsRow[]) =>
  data.map(entry => {
    return {
      id: entry.id,
      name: entry.name,
      type: entry.type,
      company: entry.company.name,
      status: entry.status,
      lastEvent: entry.lastEvent,
      enabled: entry.enabled
    };
  });

export const deriveApiCredentialStatus = (
  enabled: boolean,
  expirationDate: string | null
): API_CREDENTIAL_STATUS => {
  if (!enabled) return API_CREDENTIAL_STATUS.Disabled;

  if (expirationDate && new Date(expirationDate) < new Date())
    return API_CREDENTIAL_STATUS.Expired;

  return API_CREDENTIAL_STATUS.Active;
};

export const mapIntegrationsApiTableRowData = (
  data: ApiCredentialRow[]
): MappedApiCredentialRow[] =>
  data.map(entry => ({
    id: entry.id,
    name: entry.name,
    company: entry.company.name,
    companyId: entry.company.id,
    createdAt: entry.createdAt,
    expirationDate: entry.expirationDate,
    lastEvent: entry.lastEvent,
    enabled: entry.enabled,
    status: deriveApiCredentialStatus(entry.enabled, entry.expirationDate)
  }));

export const mapAnalysisProvidersTableRowData = (
  data: AnalysisProvidersRow[]
): MappedAnalysisProvidersRow[] =>
  data.map(entry => ({
    id: entry.id,
    name: entry.name,
    provider: entry.provider,
    company: entry.company.name,
    companyId: entry.company.id,
    createdAt: entry.createdAt,
    createdBy: entry.createdBy,
    lastEvent: entry.lastEvent,
    enabled: entry.enabled,
    status: entry.status
  }));

export const mapCompaniesTableRowData = (data: CompaniesHierarchyDataDto) => {
  data.level = 0;
  const dataToFlatten = { ...data, level: 0 };

  return flattenCompanyChildren(dataToFlatten);
};

export const mapUsersInvitationsTableRowData = (
  data: UsersInvitationsDataDto[]
): MappedUsersInvitationsDataDto[] =>
  data.map(
    ({
      id,
      email,
      role,
      invitedBy,
      invitedAt,
      updatedAt,
      company,
      invitationStatus
    }) => ({
      id,
      email,
      name: company.name,
      role,
      invitedBy,
      invitedAt,
      updatedAt,
      company,
      invitationStatus
    })
  );
